import { useQuery } from '@tanstack/react-query'
import { api } from './api'

export interface Entity {
  id: string
  kind: 'node' | 'stack' | 'service'
  docker_id: string
  name: string
  image: string | null
  labels: Record<string, string>
  first_seen: string
  last_seen: string
  status: 'active' | 'gone'
  classification_auto: string | null
  classification_override: string | null
  monitor_enabled: boolean
}

export interface NodeMetrics {
  ts: string
  cpu_pct: number | null
  cpu_iowait_pct: number | null
  load1: number | null
  load5: number | null
  mem_used: number | null
  mem_total: number | null
  mem_cached: number | null
  swap_used: number | null
  swap_total: number | null
  disk_used: number | null
  disk_total: number | null
  disk_io_read_bps: number | null
  disk_io_write_bps: number | null
  net_rx_bps: number | null
  net_tx_bps: number | null
  containers_running: number | null
}

export interface GpuSnapshot {
  gpu_index: number
  gpu_uuid: string
  gpu_name: string | null
  util_pct: number | null
  mem_used: number | null
  mem_total: number | null
  temp_c: number | null
  power_w: number | null
  ts: string
}

export interface DiskSnapshot {
  mountpoint: string
  used_bytes: number
  total_bytes: number
  fs_type: string | null
  ts: string
}

export interface Node extends Entity {
  latest_metrics: NodeMetrics | null
  gpus: GpuSnapshot[] | null
  disks: DiskSnapshot[] | null
}

export interface StackSummary {
  name: string
  services_count: number
}

export interface StackDetail {
  stack: string
  services: Entity[]
}

// Live WS events only fire on Docker events (service create/remove); metric
// ingestion publishes nothing. Without a poll, the heartbeat ages these pages
// render from Date.now() freeze at whatever they were on the last Docker event,
// so a node that stopped reporting keeps showing a healthy "hb 12 s" forever.
const NODES_REFETCH_MS = 15_000
const INVENTORY_REFETCH_MS = 60_000

export function useNodes() {
  return useQuery<Node[]>({
    queryKey: ['cluster', 'nodes'],
    queryFn: () => api<Node[]>('/api/cluster/nodes'),
    refetchInterval: NODES_REFETCH_MS,
  })
}

export function useServices() {
  return useQuery<Entity[]>({
    queryKey: ['cluster', 'services'],
    queryFn: () => api<Entity[]>('/api/cluster/services'),
    refetchInterval: INVENTORY_REFETCH_MS,
  })
}

export function useStacks() {
  return useQuery<StackSummary[]>({
    queryKey: ['cluster', 'stacks'],
    queryFn: () => api<StackSummary[]>('/api/cluster/stacks'),
    refetchInterval: INVENTORY_REFETCH_MS,
  })
}

export function useStack(name: string | undefined) {
  return useQuery<StackDetail>({
    queryKey: ['cluster', 'stack', name],
    queryFn: () => api<StackDetail>(`/api/cluster/stacks/${encodeURIComponent(name!)}`),
    enabled: !!name,
  })
}

export function useService(id: string | undefined) {
  return useQuery<Entity>({
    queryKey: ['cluster', 'service', id],
    queryFn: () => api<Entity>(`/api/cluster/services/${id!}`),
    enabled: !!id,
  })
}
